import type {
  CreateAdminStateDto,
  ReplaceAdminStateAttachmentRequirementsDto,
  UpdateAdminStateDto
} from '@idc-forms/api-client'
import type {
  V2CreateAdminStateInput,
  V2ReplaceAdminStateAttachmentRequirementsInput,
  V2UpdateAdminStateInput
} from '@idc-forms/schemas/api-v2/admin-states'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import { statesAdminV2Service } from '../services/states-admin-v2.service'

/**
 * Mutaciones de estados contra la API v2. Identifican por id público (`sta_`) y,
 * al completar, invalidan todo el árbol `['states']` (listado + summary + lookup
 * + detalle + requisitos).
 *
 * Crear/actualizar/reemplazar NO avisan por sí solas —ni éxito ni error—: el
 * editor las ENCADENA (crea el estado y después reemplaza sus requisitos) y
 * tiene que avisar UNA vez, cuando terminó de guardar todo. Un toast por
 * mutación anunciaría "estado creado" antes de saber si los requisitos se
 * guardaron, y en el error mostraría dos mensajes pisados. El aviso lo pone
 * cada consumidor en su `onSuccess`/`onError`.
 *
 * El borrado sí avisa: no encadena con nada.
 */
function useV2AccessToken() {
  const { auth } = useAuth()
  return auth?.v2AccessToken ?? ''
}

export const useCreateStateV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    // Se acepta el tipo de DOMINIO (schema Zod) y se castea en el borde del SDK:
    // el generado y el schema describen el mismo dato en runtime pero TS los ve
    // como tipos distintos.
    mutationFn: async (body: V2CreateAdminStateInput) =>
      await statesAdminV2Service.create(accessToken, body as CreateAdminStateDto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.states.all })
    }
  })
}

export const useUpdateStateV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: V2UpdateAdminStateInput }) =>
      await statesAdminV2Service.update(accessToken, id, body as UpdateAdminStateDto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.states.all })
    }
  })
}

/**
 * Reemplaza la lista ENTERA de requisitos documentales del estado. Lo que no
 * viaja se borra, así que el editor manda siempre la lista completa.
 */
export const useReplaceStateV2AttachmentRequirementsMutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async ({
      id,
      body
    }: {
      id: string
      body: V2ReplaceAdminStateAttachmentRequirementsInput
    }) =>
      await statesAdminV2Service.replaceAttachmentRequirements(
        accessToken,
        id,
        body as ReplaceAdminStateAttachmentRequirementsDto
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.states.attachmentRequirementsV2(variables.id)
      })
      void queryClient.invalidateQueries({ queryKey: queryKeys.states.all })
    }
  })
}

export const useDeleteStateV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async (id: string) => await statesAdminV2Service.remove(accessToken, id),
    onSuccess: () => {
      toast.success('Estado eliminado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.states.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}
